import React, { useCallback, useMemo } from 'react'
import { Grid, Box } from '@mui/material'
import { FormControlLabel, Radio } from '@material-ui/core'
import * as Modal from '_components/modal/generic-modal'
import { useFormContext } from 'react-hook-form'
import PropTypes from 'prop-types'

import { MultilineInput, BasicInput } from '_/components/inputs'
import { strMaxLenMessage } from '_/utils/form-validations'
import { useDialog } from '_/hooks/use-dialog'

import useStyles from './styles'

export function AiSuggestionsModal({ modalInfo, setModalInfo, onClose }) {
  const styles = useStyles()
  const {
    setValue,
    watch,
    formState: { isDirty },
  } = useFormContext()

  const { showDialog, closeDialog } = useDialog()
  const resolutions = watch('budgetResolutions')
  const resolution = modalInfo?.resolution

  const isAnyChoiceMade = useMemo(() => {
    const hasServiceDesc = modalInfo?.serviceDescription
    const hasProblemDescription = modalInfo?.problemDescription
    const hasMaterials = modalInfo?.materials && Object.keys(modalInfo.materials).length > 0

    const areAllMaterialsDecided = hasMaterials
      ? Object.keys(modalInfo.materials).every(
          index =>
            modalInfo?.acceptMaterials?.[index] !== null &&
            modalInfo?.acceptMaterials?.[index] !== undefined
        )
      : true

    return (
      (!hasServiceDesc || modalInfo?.acceptServiceDescription !== null) &&
      (!hasProblemDescription || modalInfo?.acceptProblemDescription !== null) &&
      areAllMaterialsDecided
    )
  }, [modalInfo])

  const updateFields = useCallback(() => {
    const resolutionIndex = resolutions?.findIndex(r => r.id === resolution?.id)
    if (resolutionIndex !== undefined && resolutionIndex !== -1) {
      if (modalInfo?.acceptServiceDescription) {
        const serviceDescriptionSuggestion = watch('serviceDescriptionSuggestion')

        setValue(
          `budgetResolutions.${resolutionIndex}.serviceDescription`,
          serviceDescriptionSuggestion,
          {
            shouldDirty: true,
          }
        )
      }

      if (modalInfo?.acceptProblemDescription) {
        const problemDescriptionSuggestion = watch('problemDescriptionSuggestion')
        setValue(
          `budgetResolutions.${resolutionIndex}.problemDescription`,
          problemDescriptionSuggestion,
          {
            shouldDirty: true,
          }
        )
      }

      if (modalInfo?.acceptMaterials && Object.keys(modalInfo.acceptMaterials).length > 0) {
        const materialsSuggestion = watch('materialsSuggestion') || {}

        Object.entries(modalInfo.acceptMaterials).forEach(([materialIndex, accepted]) => {
          if (accepted !== true) return

          const suggestion = materialsSuggestion?.[materialIndex]
          if (!suggestion) return

          setValue(
            `budgetResolutions.${resolutionIndex}.materials.${materialIndex}.description`,
            suggestion,
            { shouldDirty: true }
          )
        })
      }

      onClose()
    }
  }, [resolution, modalInfo, setValue, watch, onClose])

  const handleCancel = () => {
    if (isDirty) {
      showDialog({
        type: 'warning',
        subject: 'Você tem certeza que deseja cancelar estas alterações?',
        labelApprove: 'Sim',
        labelCancel: 'Não',
        onApprove: () => {
          closeDialog()
          onClose()
        },
        onCancel: closeDialog,
      })
    } else {
      onClose()
    }
  }

  return (
    <Modal.Root open={modalInfo?.open} onClose={onClose} maxWidth="xl">
      <Modal.TitleModal title="Sugestões da IA de Correção e Aprimoramento" />
      <Modal.Content className={styles.dialogContent}>
        <Grid container columnSpacing={6} rowSpacing={4}>
          <Grid item xs={6}>
            <MultilineInput
              defaultValue={resolution?.problemDescription}
              name="problemDescription"
              label="Diagnóstico do problema"
              rules={{ maxLength: { value: 5000, message: strMaxLenMessage(5000) } }}
              minRows={5}
              maxRows={100}
              disabled
            />
          </Grid>
          <Grid item xs={6}>
            <MultilineInput
              defaultValue={modalInfo?.problemDescription}
              name="problemDescriptionSuggestion"
              placeholder="Sem sugestão da IA"
              label="IA - Sugestão de Diagnóstico para o problema"
              rules={{ maxLength: { value: 5000, message: strMaxLenMessage(5000) } }}
              minRows={5}
              maxRows={100}
            />

            <Box sx={{ display: 'flex', gap: 2.5, mt: -2 }}>
              <FormControlLabel
                control={
                  <Radio
                    checked={modalInfo?.acceptProblemDescription === true}
                    onChange={() =>
                      setModalInfo(prev => ({ ...prev, acceptProblemDescription: true }))
                    }
                    disabled={!modalInfo?.problemDescription}
                  />
                }
                label="Aceitar sugestão"
                disabled={!modalInfo?.problemDescription}
              />
              <FormControlLabel
                control={
                  <Radio
                    checked={modalInfo?.acceptProblemDescription === false}
                    onChange={() =>
                      setModalInfo(prev => ({ ...prev, acceptProblemDescription: false }))
                    }
                    disabled={!modalInfo?.problemDescription}
                  />
                }
                label="Recusar sugestão"
                disabled={!modalInfo?.problemDescription}
              />
            </Box>
          </Grid>

          <Grid item xs={6}>
            <MultilineInput
              defaultValue={resolution?.serviceDescription}
              name="serviceDescription"
              label="Solução do problema"
              rules={{ maxLength: { value: 5000, message: strMaxLenMessage(5000) } }}
              minRows={5}
              maxRows={100}
              disabled
            />
          </Grid>
          <Grid item xs={6}>
            <MultilineInput
              defaultValue={modalInfo?.serviceDescription}
              placeholder="Sem sugestão da IA"
              name="serviceDescriptionSuggestion"
              label="IA - Sugestão de Solução para o problema"
              rules={{ maxLength: { value: 5000, message: strMaxLenMessage(5000) } }}
              minRows={5}
              maxRows={100}
            />
            <Box sx={{ display: 'flex', gap: 2.5, mt: -2 }}>
              <FormControlLabel
                control={
                  <Radio
                    checked={modalInfo?.acceptServiceDescription === true}
                    onChange={() =>
                      setModalInfo(prev => ({ ...prev, acceptServiceDescription: true }))
                    }
                    disabled={!modalInfo?.serviceDescription}
                  />
                }
                label="Aceitar sugestão"
                disabled={!modalInfo?.serviceDescription}
              />
              <FormControlLabel
                control={
                  <Radio
                    checked={modalInfo?.acceptServiceDescription === false}
                    onChange={() =>
                      setModalInfo(prev => ({ ...prev, acceptServiceDescription: false }))
                    }
                    disabled={!modalInfo?.serviceDescription}
                  />
                }
                label="Recusar sugestão"
                disabled={!modalInfo?.serviceDescription}
              />
            </Box>
          </Grid>

          {Object.entries(modalInfo?.materials ?? {}).map(([materialIndex, materialText]) => {
            const materialPosition = Number(materialIndex)
            const currentMaterial = resolution?.materials?.[materialPosition]?.description ?? ''
            return (
              <React.Fragment key={materialIndex}>
                <Grid item xs={6}>
                  <BasicInput
                    defaultValue={currentMaterial}
                    textArea
                    name={`materialsCurrent.${materialIndex}`}
                    label={`Material ${materialPosition + 1}`}
                    rows={1}
                    disabled
                  />
                </Grid>
                <Grid item xs={6}>
                  <BasicInput
                    defaultValue={materialText}
                    textArea
                    name={`materialsSuggestion.${materialIndex}`}
                    label={`IA - Sugestão de Material ${Number(materialIndex) + 1}`}
                    rows={1}
                  />
                  <Box sx={{ display: 'flex', gap: 2.5, mt: -2 }}>
                    <FormControlLabel
                      control={
                        <Radio
                          checked={modalInfo?.acceptMaterials?.[materialIndex] === true}
                          onChange={() =>
                            setModalInfo(prev => ({
                              ...prev,
                              acceptMaterials: {
                                ...prev.acceptMaterials,
                                [materialIndex]: true,
                              },
                            }))
                          }
                          disabled={!modalInfo?.materials?.[materialIndex]}
                        />
                      }
                      label="Aceitar sugestão"
                      disabled={!modalInfo?.materials?.[materialIndex]}
                    />
                    <FormControlLabel
                      control={
                        <Radio
                          checked={modalInfo?.acceptMaterials?.[materialIndex] === false}
                          onChange={() =>
                            setModalInfo(prev => ({
                              ...prev,
                              acceptMaterials: {
                                ...prev.acceptMaterials,
                                [materialIndex]: false,
                              },
                            }))
                          }
                          disabled={!modalInfo?.materials?.[materialIndex]}
                        />
                      }
                      label="Recusar sugestão"
                      disabled={!modalInfo?.materials?.[materialIndex]}
                    />
                  </Box>
                </Grid>
              </React.Fragment>
            )
          })}
        </Grid>
      </Modal.Content>
      <Modal.Actions>
        <Modal.ButtonRed onClick={handleCancel}>Cancelar</Modal.ButtonRed>
        <Modal.ButtonFullBlue onClick={updateFields} disabled={!isAnyChoiceMade}>
          Salvar
        </Modal.ButtonFullBlue>
      </Modal.Actions>
    </Modal.Root>
  )
}

AiSuggestionsModal.propTypes = {
  modalInfo: PropTypes.shape({}).isRequired,
  setModalInfo: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
}
